import { createServerFn } from "@tanstack/react-start";
import { and, eq } from "drizzle-orm";
import { nanoid } from "nanoid";
import { z } from "zod";
import { projectInvitation, projectMember } from "~/lib/db/schema";

const tokenSchema = z.object({
  token: z.string(),
});

export const $getInvitationByToken = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => tokenSchema.parse(data))
  .handler(async ({ data }) => {
    const { db } = await import("~/lib/server/helpers.server");
    const { auth } = await import("~/lib/auth/auth");
    const { getRequest } = await import("@tanstack/react-start/server");

    const invitation = await db.query.projectInvitation.findFirst({
      where: eq(projectInvitation.token, data.token),
      with: {
        project: true,
        invitedByUser: true,
      },
    });

    if (!invitation) {
      return { error: "Invitation not found" };
    }

    // Check if expired
    if (invitation.expiresAt < new Date()) {
      // Mark as expired if still pending
      if (invitation.status === "pending") {
        await db
          .update(projectInvitation)
          .set({ status: "expired" })
          .where(eq(projectInvitation.id, invitation.id));
      }
      return { error: "This invitation has expired" };
    }

    if (invitation.status === "accepted") {
      return { error: "This invitation has already been accepted" };
    }

    if (invitation.status === "cancelled") {
      return { error: "This invitation has been cancelled" };
    }

    if (invitation.status === "expired") {
      return { error: "This invitation has expired" };
    }

    // Get session to check if user is logged in
    const session = await auth.api.getSession({
      headers: getRequest().headers,
    });

    const isLoggedIn = !!session?.user;
    const userEmail = session?.user?.email?.toLowerCase();
    const invitationEmail = invitation.email.toLowerCase();
    const emailMatches = userEmail === invitationEmail;

    return {
      invitation: {
        id: invitation.id,
        email: invitation.email,
        role: invitation.role,
        expiresAt: invitation.expiresAt,
        projectTitle: invitation.project.title,
        projectDescription: invitation.project.description,
        invitedByName: invitation.invitedByUser.name,
      },
      isLoggedIn,
      emailMatches,
      currentUserEmail: userEmail,
    };
  });

export const $acceptInvitation = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => tokenSchema.parse(data))
  .handler(async ({ data }) => {
    const { db } = await import("~/lib/server/helpers.server");
    const { auth } = await import("~/lib/auth/auth");
    const { getRequest } = await import("@tanstack/react-start/server");

    const session = await auth.api.getSession({
      headers: getRequest().headers,
    });

    if (!session?.user) {
      throw new Error("You must be logged in to accept an invitation");
    }

    const invitation = await db.query.projectInvitation.findFirst({
      where: eq(projectInvitation.token, data.token),
    });

    if (!invitation) {
      throw new Error("Invitation not found");
    }

    if (invitation.status !== "pending") {
      throw new Error(
        invitation.status === "accepted"
          ? "This invitation has already been accepted"
          : invitation.status === "cancelled"
            ? "This invitation has been cancelled"
            : "This invitation has expired",
      );
    }

    if (invitation.expiresAt < new Date()) {
      await db
        .update(projectInvitation)
        .set({ status: "expired" })
        .where(eq(projectInvitation.id, invitation.id));
      throw new Error("This invitation has expired");
    }

    // Strict email matching
    const userEmail = session.user.email.toLowerCase();
    const invitationEmail = invitation.email.toLowerCase();

    if (userEmail !== invitationEmail) {
      throw new Error(
        `This invitation was sent to ${invitation.email}. You are logged in as ${session.user.email}. Please log in with the correct account.`,
      );
    }

    // Check if already a member
    const existingMember = await db.query.projectMember.findFirst({
      where: and(
        eq(projectMember.projectId, invitation.projectId),
        eq(projectMember.userId, session.user.id),
      ),
    });

    if (existingMember) {
      // Mark invitation as accepted anyway
      await db
        .update(projectInvitation)
        .set({
          status: "accepted",
          acceptedAt: new Date(),
          acceptedBy: session.user.id,
        })
        .where(eq(projectInvitation.id, invitation.id));

      return { projectId: invitation.projectId, alreadyMember: true };
    }

    // Create membership and update invitation in a transaction
    await db.transaction(async (tx) => {
      await tx.insert(projectMember).values({
        id: nanoid(),
        projectId: invitation.projectId,
        userId: session.user.id,
        role: invitation.role,
        addedBy: invitation.invitedBy,
      });

      await tx
        .update(projectInvitation)
        .set({
          status: "accepted",
          acceptedAt: new Date(),
          acceptedBy: session.user.id,
        })
        .where(eq(projectInvitation.id, invitation.id));
    });

    return { projectId: invitation.projectId, alreadyMember: false };
  });

export const $declineInvitation = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => tokenSchema.parse(data))
  .handler(async ({ data }) => {
    const { db } = await import("~/lib/server/helpers.server");
    const { auth } = await import("~/lib/auth/auth");
    const { getRequest } = await import("@tanstack/react-start/server");

    const session = await auth.api.getSession({
      headers: getRequest().headers,
    });

    if (!session?.user) {
      throw new Error("You must be logged in to decline an invitation");
    }

    const invitation = await db.query.projectInvitation.findFirst({
      where: eq(projectInvitation.token, data.token),
    });

    if (!invitation) {
      throw new Error("Invitation not found");
    }

    if (invitation.status === "accepted") {
      throw new Error("This invitation has already been accepted");
    }

    if (invitation.status === "cancelled") {
      return { success: true };
    }

    if (invitation.status === "expired") {
      throw new Error("This invitation has expired");
    }

    if (invitation.expiresAt < new Date()) {
      await db
        .update(projectInvitation)
        .set({ status: "expired" })
        .where(eq(projectInvitation.id, invitation.id));
      throw new Error("This invitation has expired");
    }

    const userEmail = session.user.email.toLowerCase();
    const invitationEmail = invitation.email.toLowerCase();
    if (userEmail !== invitationEmail) {
      throw new Error(
        `This invitation was sent to ${invitation.email}. You are logged in as ${session.user.email}. Please log in with the correct account.`,
      );
    }

    await db
      .update(projectInvitation)
      .set({ status: "cancelled" })
      .where(eq(projectInvitation.id, invitation.id));

    return { success: true };
  });
